/* GENERATED FILE — do not edit.
   Source: shared-ui/ui/Avatar.js
   Run "node shared-ui/sync.js" after editing the source. */
import React, { useMemo, useState } from 'react'
import './avatar.css'

/*
 * Avatar — one face component for the whole app.
 *
 * The media endpoint (`files/{UUID}/{tag}`) answers with the player's photo and
 * 404s for everyone who never uploaded one, so a fallback is not optional: the
 * component swaps a broken image for an initials monogram.
 *
 * Two things this deliberately does NOT do:
 *
 *   1. It never puts Date.now() in the URL. A fresh cache-buster on every render
 *      makes the browser refetch the same face each time React re-renders, which
 *      turns any list into a flicker machine. The URL is memoised on uuid/src and
 *      uses the same stable tag the rest of the app already sends.
 *   2. It never re-renders an <img> whose src has already failed. Instead of a
 *      boolean we remember *which* URL broke, so onError cannot fire in a loop —
 *      and a genuinely new src is retried exactly once.
 */

/* Matches the tag used by Challenges / PlayersList: stable, so the HTTP cache works.
   Photo *changes* bust themselves — Profile.js and Login.js store a usrPhoto URL
   that already carries its own upload timestamp, and that URL arrives here as `src`. */
const CACHE_TAG = '123'

/* Punctuation only — anything else (Latin, Greek, Cyrillic, digits) is a usable initial. */
const NOISE = /["'`´.,;:!?()[\]{}<>/\\|@#$%^&*_+=~-]/g

/* "Giannis M." -> "GM" · "Giannis" -> "G" · "" -> "?" */
export function initialsFrom(name) {
  const letters = String(name == null ? '' : name)
    .trim()
    .split(/\s+/)
    .map((part) => part.replace(NOISE, ''))
    .filter(Boolean)
    .map((part) => part.charAt(0))

  if (!letters.length) return '?'
  if (letters.length === 1) return letters[0].toUpperCase()
  return (letters[0] + letters[letters.length - 1]).toUpperCase()
}

export default function Avatar({ uuid, name, src, size = 34, className }) {
  /* Keyed on uuid/src so the URL — and therefore the <img> — is stable across renders. */
  const url = useMemo(() => {
    const explicit = typeof src === 'string' ? src.trim() : ''
    if (explicit) return explicit

    const id = typeof uuid === 'string' ? uuid.trim() : uuid ? String(uuid).trim() : ''
    if (!id) return ''

    return (process.env.REACT_APP_API_SERVER || '') + 'files/' + id + '/' + CACHE_TAG
  }, [uuid, src])

  /* Holds the exact URL that failed, not a boolean: a new URL is therefore
     un-broken by construction, and re-setting the same one is a no-op. */
  const [brokenUrl, setBrokenUrl] = useState(null)

  const showImage = !!url && brokenUrl !== url
  const initials = initialsFrom(name)
  const label = String(name || '').trim()

  const cls = 'av' + (className ? ' ' + className : '')
  const box = { '--av-size': (Number(size) > 0 ? Number(size) : 34) + 'px' }

  if (!showImage) {
    return (
      <span
        className={cls + ' av-empty'}
        style={box}
        role="img"
        aria-label={label || 'No photo'}
        title={label || undefined}
      >
        <span className="av-mono" aria-hidden="true">{initials}</span>
      </span>
    )
  }

  return (
    <span className={cls} style={box}>
      <img
        className="av-img"
        src={url}
        alt={label}
        loading="lazy"
        decoding="async"
        draggable="false"
        onError={() => setBrokenUrl(url)}
      />
    </span>
  )
}
